import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gavel, Wand2, Copy, Printer, FileText, Lock, Sparkles, ScrollText, CheckCircle2 } from "lucide-react";

function LockedGate({ addonName }) {
  return (
    <div className="hoa-card royal-ring p-10 text-center relative overflow-hidden" data-testid="legal-locked">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-primary/25 blur-3xl" />
      <div className="relative">
        <Lock className="w-8 h-8 text-gold mx-auto mb-3" />
        <h2 className="text-2xl font-semibold mb-2">{addonName} is a premium add-on</h2>
        <p className="no-gold text-foreground/75 max-w-xl mx-auto mb-6">
          Draft demand letters, notices, and liens in seconds — with self-performing management with AI-assist AI Saint boilerplate tuned for your community.
          Start your 14-day free trial from Subscription → Add-ons.
        </p>
        <a href="/subscription" className="inline-flex items-center gap-2 bg-gold text-secondary-foreground px-5 py-3 rounded-full font-semibold hover:brightness-110 glow-gold">
          <Sparkles className="w-4 h-4" /> Start free trial
        </a>
      </div>
    </div>
  );
}

export default function LegalSuite() {
  const { user } = useAuth();
  const isBoard = user?.role === "admin" || user?.role === "board";
  const [locked, setLocked] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [docs, setDocs] = useState([]);
  const [owners, setOwners] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [activeEscalations, setActiveEscalations] = useState([]);
  const [escalationLocked, setEscalationLocked] = useState(false);

  const [open, setOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ template_type: "demand_letter", owner_id: "", context: "", state: "LA" });

  const [escOpen, setEscOpen] = useState(false);
  const [escForm, setEscForm] = useState({ template_id: "delinquent_dues", owner_id: "", notes: "" });

  const load = useCallback(async () => {
    try {
      const [t, d] = await Promise.all([api.get("/legal-templates"), api.get("/legal-documents")]);
      setTemplates(t.data);
      setDocs(d.data);
      setLocked(false);
      if (isBoard) {
        const { data: users } = await api.get("/users");
        setOwners(users.filter((u) => u.role === "owner"));
      }
    } catch (err) {
      if (err.response?.status === 402) setLocked(true);
      else console.error("LegalSuite templates load failed:", err);
    }
    try {
      const [e, ae] = await Promise.all([api.get("/escalations"), api.get("/escalations/active")]);
      setEscalations(e.data); setActiveEscalations(ae.data);
      setEscalationLocked(false);
    } catch (err) {
      if (err.response?.status === 402) setEscalationLocked(true);
      else console.error("LegalSuite escalations load failed:", err);
    }
  }, [isBoard]);
  useEffect(() => { load(); }, [load]);

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const { data } = await api.post("/legal-templates/generate", form);
      toast.success("Document generated");
      setSelectedDoc(data);
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate");
    } finally { setGenerating(false); }
  };

  const startEscalation = async (e) => {
    e.preventDefault();
    try {
      await api.post("/escalations/start", escForm);
      toast.success("Escalation started");
      setEscOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not start"); }
  };

  const toggleStep = async (eid, step_id, done) => {
    try {
      await api.patch(`/escalations/${eid}/step`, { step_id, done });
      load();
    } catch (err) { if (process.env.NODE_ENV !== "production") console.warn("swallowed:", err?.message || err); }
  };

  const copyBody = async () => {
    if (!selectedDoc) return;
    await navigator.clipboard.writeText(selectedDoc.body);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-6" data-testid="legal-suite-page">
      <div>
        <div className="flex items-center gap-2 text-gold text-sm font-medium mb-2">
          <Gavel className="w-4 h-4" /> Legal Suite
        </div>
        <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight">Firm, professional, ready in a minute.</h1>
        <p className="no-gold text-foreground/75 mt-2 max-w-2xl">
          Generate demand letters, notices, and liens with AI-assisted drafting. Run gov-doc–aligned escalations with
          citations on every step.
        </p>
      </div>

      <Tabs defaultValue="templates">
        <TabsList className="bg-card border border-border" data-testid="legal-tabs">
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="library" data-testid="tab-library">Generated documents</TabsTrigger>
          <TabsTrigger value="escalations" data-testid="tab-escalations">Escalation workflows</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-6 space-y-4">
          {locked ? <LockedGate addonName="Legal Templates Library" /> : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setForm({ ...form, template_type: t.id }); setOpen(true); }}
                    disabled={!isBoard}
                    className="hoa-card p-6 text-left hover:border-gold transition-colors disabled:opacity-60"
                    data-testid={`tpl-${t.id}`}
                  >
                    <ScrollText className="w-6 h-6 text-gold mb-3" />
                    <div className="no-gold font-semibold">{t.label}</div>
                    <div className="text-xs text-muted-foreground mt-1">Tap to draft</div>
                  </button>
                ))}
                {templates.length === 0 && (
                  <div className="col-span-full text-center text-muted-foreground p-8" data-testid="tpl-empty">No templates yet.</div>
                )}
              </div>

              {selectedDoc && (
                <div className="hoa-card p-6 print:bg-white print:text-black" data-testid="generated-doc-card">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-gold-soft">{selectedDoc.label}</div>
                      <h3 className="no-gold text-lg font-semibold" data-testid="gen-doc-title">{selectedDoc.owner_name} · {selectedDoc.state}</h3>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={copyBody} data-testid="copy-legal-btn">
                        <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-legal-btn">
                        <Printer className="w-3.5 h-3.5 mr-1.5" /> Print
                      </Button>
                    </div>
                  </div>
                  <pre className="no-gold whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-foreground/90" data-testid="gen-doc-body">
                    {selectedDoc.body}
                  </pre>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="library" className="mt-6">
          {locked ? <LockedGate addonName="Legal Templates Library" /> : (
            <div className="space-y-3">
              {docs.length === 0 ? (
                <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="legal-library-empty">
                  No documents generated yet.
                </div>
              ) : (
                docs.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDoc(d)}
                    className="w-full text-left hoa-card p-5 hover:border-gold transition-colors"
                    data-testid={`legal-doc-${d.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-gold-soft">{d.label}</div>
                        <div className="no-gold font-semibold">{d.owner_name}{d.state ? ` · ${d.state}` : ""}</div>
                        <div className="text-xs text-muted-foreground mt-1">by {d.generated_by} · {new Date(d.generated_at).toLocaleString()}</div>
                      </div>
                      <FileText className="w-5 h-5 text-gold" />
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="escalations" className="mt-6 space-y-6">
          {escalationLocked ? <LockedGate addonName="Escalation Workflows" /> : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Active escalations</h3>
                {isBoard && (
                  <Dialog open={escOpen} onOpenChange={setEscOpen}>
                    <DialogTrigger asChild>
                      <Button className="rounded-full h-11 bg-gold text-secondary-foreground hover:brightness-110" data-testid="start-escalation-btn">
                        <Wand2 className="w-4 h-4 mr-2" /> Start escalation
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Start an escalation workflow</DialogTitle></DialogHeader>
                      <form onSubmit={startEscalation} className="space-y-4" data-testid="escalation-form">
                        <div>
                          <Label>Workflow</Label>
                          <Select value={escForm.template_id} onValueChange={(v) => setEscForm({ ...escForm, template_id: v })}>
                            <SelectTrigger className="mt-1.5 h-11" data-testid="esc-template"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {escalations.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Owner</Label>
                          <Select value={escForm.owner_id} onValueChange={(v) => setEscForm({ ...escForm, owner_id: v })}>
                            <SelectTrigger className="mt-1.5 h-11" data-testid="esc-owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
                            <SelectContent>
                              {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.unit_number ? ` · ${o.unit_number}` : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea value={escForm.notes} onChange={(e) => setEscForm({ ...escForm, notes: e.target.value })} className="mt-1.5" data-testid="esc-notes" />
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={!escForm.owner_id} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="esc-submit">Start</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
              </div>

              {activeEscalations.length === 0 ? (
                <div className="hoa-card p-10 text-center text-muted-foreground" data-testid="active-esc-empty">
                  No active escalations.
                </div>
              ) : (
                <div className="space-y-4">
                  {activeEscalations.map((e) => (
                    <div key={e.id} className="hoa-card p-6" data-testid={`active-esc-${e.id}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="no-gold font-semibold">{e.title}</div>
                          <div className="text-xs text-muted-foreground">{e.owner_name}{e.owner_unit ? ` · ${e.owner_unit}` : ""} · started {new Date(e.started_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {e.steps.map((s) => (
                          <div key={s.id} className={`flex items-start gap-3 p-3 rounded-lg border ${s.done ? "border-gold bg-[hsl(var(--gold)/0.06)]" : "border-border bg-accent/20"}`} data-testid={`step-${s.id}`}>
                            <button
                              onClick={() => isBoard && toggleStep(e.id, s.id, !s.done)}
                              disabled={!isBoard}
                              className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${s.done ? "bg-gold text-secondary-foreground" : "border border-border"}`}
                              data-testid={`step-toggle-${s.id}`}
                            >
                              {s.done && <CheckCircle2 className="w-4 h-4" />}
                            </button>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[10px] uppercase tracking-widest text-gold font-semibold font-mono">Day {s.day}</span>
                                <span className="no-gold text-sm font-medium">{s.action}</span>
                              </div>
                              <div className="text-[11px] text-muted-foreground italic">Gov-doc citation: {s.citation}</div>
                              {s.done && s.done_by && <div className="text-[10px] text-gold mt-1">✓ Completed by {s.done_by} · {new Date(s.done_at).toLocaleDateString()}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div>
                <h4 className="text-lg font-semibold mb-3">Templates</h4>
                <div className="grid md:grid-cols-3 gap-3">
                  {escalations.map((e) => (
                    <div key={e.id} className="hoa-card p-5" data-testid={`esc-tpl-${e.id}`}>
                      <div className="no-gold font-semibold">{e.title}</div>
                      <div className="text-xs text-muted-foreground mt-1">{e.steps.length} steps · {e.gov_doc_ref}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Generate Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Draft a legal document</DialogTitle></DialogHeader>
          <form onSubmit={generate} className="space-y-4" data-testid="legal-gen-form">
            <div>
              <Label>Template</Label>
              <Select value={form.template_type} onValueChange={(v) => setForm({ ...form, template_type: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="gen-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Owner</Label>
              <Select value={form.owner_id} onValueChange={(v) => setForm({ ...form, owner_id: v })}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="gen-owner"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>
                  {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.unit_number ? ` · ${o.unit_number}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1.5 h-11" placeholder="LA" data-testid="gen-state" />
            </div>
            <div>
              <Label>Facts of the case</Label>
              <Textarea required value={form.context} onChange={(e) => setForm({ ...form, context: e.target.value })} className="mt-1.5 min-h-[140px]" placeholder="Owner has been delinquent for 45 days on $1,200 in dues. Multiple friendly reminders sent…" data-testid="gen-context" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={generating || !form.owner_id} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="gen-submit">
                <Wand2 className="w-4 h-4 mr-2" /> {generating ? "Drafting…" : "Draft with AI"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
